import {Avatar, DatePicker, Input, message, Radio} from 'antd'
import {getId} from 'apis/model/base'
import DidOnMount from 'components/DidOnMount'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Formik, FormikConsumer} from 'formik'
import {isValidDate, parseDayjs} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {IoCalendarOutline, IoCopyOutline, IoWarning} from 'react-icons/io5'
import {compose, mapProps, pure} from 'recompose'
import {GenderTypes} from 'views/AppLocals/appFieldTypes'
import {gConfigs} from 'views/AppLocals/configs'
import {CourseClassSelector} from 'views/AppLocals/Course/components/CourseClassSelector'
import {CourseStudentList} from 'views/AppLocals/Course/Student/StudentPicker'
import {appClassNames} from 'views/AppLocals/custom/appClassNames'
import {NullAsDefault, renderIf, renderSelf, shallowDiff} from 'views/Shared'
import {EnrollmentInfo} from './EnrollmentInfor'
import {renderIfElse} from "../../../Shared";
import {IdentifierTypes} from "../../../AppLocals/appFieldTypes";
import {createControlledFormField} from "../../../AppLocals/factory/createFormField";
import {showDeleteConfirmDialog} from "../../../AppLocals/factory/createConfirmDialog";
import copyToClipboard from "copy-to-clipboard";
import getTitle from "../../../../helpers/getTitle";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {FaUserGraduate} from "react-icons/fa6";
import {DoubleValueItem} from 'components/common/DoubleValueItem'
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {ItemInformation} from 'components/common/ItemInformation'
import {renderEmptyHolderMember} from "../../../AppLocals/CatholicHousehold/functions/renderEmptyHolderMember";
import {getType} from "../../../../apis/model/base";
import getAliasName from "../../../../helpers/getAliasName";
import {GoDotFill} from "react-icons/go";
import {BsInfoCircleFill} from "react-icons/bs";
import Translate from "../../../../modules/local/Translate";
import LookupField from "../../../../components/LookupField";
import {SaintsOutlineSvg} from "../../../../components/Svgs/SaintsPlaceholder";
import {formatDate} from "../../../../helpers/datetimeHelpers";
import dayjs from "dayjs";


const student_modes = {
    select: 'select',
    create: 'create',
}

const information_schema = [
    {name: 'student_idcode'},
    {name: 'gender'},
    {name: 'patron_saint_name'},
    {name: 'patron_saint_id'},
    {name: 'fullname'},
    {name: 'contact_phone'},
    {name: 'contact_email'},
    {name: 'identifier_type'},
    {name: 'identifier_code'},
    {
        name: 'date_of_birth',
        transform: (value) => (value ? parseDayjs(value) : undefined),
    },
]

function isDisabled(name, value) {
    switch (name) {
        case 'date_of_birth':
            return isValidDate(value, gConfigs.dateFormat)
        default:
            break
    }
    return !!value
}

const withStudentInfo = (name, {values, setValues = NullAsDefault} = {}) => {
    const {student, student_info} = values ?? {}
    const info_field = _.find(information_schema, {name})
    const {defaultValue, transform = renderSelf} = info_field ?? {}
    const raw_value = _.get(student_info, info_field?.name)
    const value = transform(raw_value) ?? defaultValue

    return {
        name,
        value,
        disabled: isDisabled(name, _.get(student, name)),
        onChange: (event) => {
            const {name, value} = event.target ?? {}
            setValues({
                ...values,
                student_info: {
                    ...(student_info ?? {}),
                    [name]: value,
                },
            })
        },
    }
}

const CourseInformation = ({item}) => {
    const {title, time_open, time_close} = item ?? {}

    return (
        <div className="flex flex-col gap-1 ml-3">
            <div className="font-bold text-sm text-color-000 leading-tight">
                {title}
            </div>
            {renderIf(
                _.some([time_open, time_close]),
                <DoubleValueItem
                    showIcon={true}
                    labelFirst="time"
                    valueFirst={formatDate(time_open, 'DD/MM/YYYY')}
                    labelSecond={time_close ? ' - ' : ''}
                    valueSecond={formatDate(time_close, 'DD/MM/YYYY')}
                    className="text-xs italic"
                />
            )}
        </div>
    )
}

const BlocWithHeader = ({title, children}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-1 mt-3">
            <div className="flex items-center gap-2">
                <BsInfoCircleFill size={14} className="text-color-400"/>
                <span className="font-medium text-sm text-color-300 tracking-wide">
            {t(title)}
          </span>
            </div>
            {children}
        </div>
    )
}

const MetaInformation = ({item}) => {
    const t = useTranslate()
    item = item ?? []
    const emptyMetadata = item.every(e => e.answer_value === null)

    return (
        <BlocWithHeader title="additional information">
            {renderIfElse(
                !emptyMetadata,
                item.map((eee, iii) => {
                    let answer_value = eee.answer_value;
                    if (eee.ctrl_type === 'toggleField') {
                        answer_value = Number(eee.answer_value) === 1 ? t('yes') : t('no')
                    } else if (eee.ctrl_type === 'checkboxField') {
                        answer_value = (eee.answer_value ?? []).join("; ")
                    } else if (eee.ctrl_type === 'dateField') {
                        answer_value = formatDate(eee.answer_value, "DD/MM/YYYY")
                    }
                    return (
                        renderIf(
                            eee.answer_value,
                            <div key={iii} className="ml-2 space-x-2 text-sm">
                                <span className="text-color-400 italic">{`- ${eee.title} : `}</span>
                                <span className="font-semibold text-color-100">
                          {answer_value}
                        </span>
                            </div>
                        )
                    )
                }),
                renderEmptyHolderMember("no data is available now", "the registrant may not have answered those questions yet")
            )}
        </BlocWithHeader>
    )
}

export const formSchema = {
    info: {
        children: [
            {
                name: 'information',
                component: mapProps(({value}) => ({
                    item: value,
                }))(EnrollmentInfo),
            },
        ],
    },
    student_list: {
        children: [
            {
                name: 'student_info',
                component: mapProps(({name, form, onChange}) => {
                    const {owner_id, owner_type, student, information} = form?.values ?? {}
                    const field_names = information_schema.map(({name}) => name)
                    return {
                        name,
                        onChange,
                        owner_id,
                        owner_type,
                        information,
                        field_names,
                        value: getId(student),
                    }
                })(CourseStudentList),
            },
        ],
    },
    notes: {
        title: 'notes',
        children: [
            {
                name: 'notes',
                component: compose(
                    withTranslate,
                    mapProps(({onChange, value, translate}) => ({
                        onChange,
                        value,
                        rows: 3,
                        name: 'notes',
                        placeholder: translate('notes'),
                    })),
                    pure
                )(Input.TextArea),
            },
        ],
    },
    patron_saint__fullname: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        itemClassNames: ['col-span-1', 'col-span-1 md:col-span-2'],
        children: [
            {
                label: 'holy name',
                name: 'patron_saint',
                component: ({name, value, onChange, form}) => (
                    <Translate>
                        {(t) => (
                            <LookupField
                                title={t('choose patron saint')}
                                createIfNotExists={true}
                                defaultValue={form?.values?.patron_saint_name}
                                selectedOption={form?.values?.patron_saint}
                                apiPath={`/lookup/saint/get-by-types`}
                                name={name}
                                OptionWrapper={({children, ...props}) =>

                                    <div className="p-3 flex items-center gap-2 lookup-item" {...props}>
                                        {children}
                                    </div>
                                }
                                renderOption={(o, i) => {
                                    return (
                                        <div key={i} className="flex items-center flex-1 gap-3">
                                            <Avatar
                                                size={50}
                                                src={o.avatar}
                                                className="flex flex-center background-200 rounded-l-lg rounded-r-0"
                                                icon={<SaintsOutlineSvg size={30} style={{color: '#dddddd'}}/>}
                                            />
                                            <div className="flex flex-col flex-1">
                                                <span className="font-bold text-sm text-color-000 leading-snug">
                                                    {`${o.common_name || o.name} (${o.patron_saint_feast_day || o.feast_day})`}
                                                </span>
                                                {renderIf(
                                                    o?.description,
                                                    <span className="text-xs text-color-400 italic max-lines-1">
                                                        {o?.description}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )
                                }}
                                handleChange={(e) => {
                                    form.setValues({
                                        ...form.values,
                                        patron_saint: e.target,
                                        patron_saint_name: e.target.common_name || e.target.value,
                                        patron_saint_feast_day: e.target.feast_day,
                                        patron_saint_id: e.target.id
                                    })
                                }}
                            />
                        )}
                    </Translate>
                ),
            },
            {
                label: 'fullname',
                name: 'fullname',
                required: true,
                component: compose(
                    withTranslate,
                    mapProps(({name, form, translate}) => {
                        const {value, onChange, disabled} = withStudentInfo(name, form) ?? {}
                        return {
                            name,
                            value,
                            disabled,
                            onChange,
                            placeholder: translate('fullname'),
                        }
                    })
                )(Input),
            },
        ],
    },
    fullname: {
        title: 'fullname',
        children: [
            {
                name: 'fullname',
                component: compose(
                    withTranslate,
                    mapProps(({name, form, translate}) => {
                        const {value, onChange, disabled} =
                        withStudentInfo(name, form) ?? {}
                        return {
                            name,
                            value,
                            disabled,
                            onChange,
                            placeholder: translate('fullname'),
                        }
                    })
                )(Input),
            },
        ],
    },
    info_group_1: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'date of birth',
                name: 'date_of_birth',
                component: compose(
                    withTranslate,
                    mapProps(({name, form, translate}) => {
                        const {value, onChange, disabled} =
                        withStudentInfo(name, form) ?? {}
                        return {
                            name,
                            value,
                            disabled,
                            className: 'w-full',
                            format: gConfigs.dateFormat,
                            suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                            placeholder: `${translate('date of birth')} "DD/MM/YYYY"`,
                            // 🔹 Giới hạn ngày
                            disabledDate: (current) => {
                                const maxDate = dayjs().subtract(5, 'year');
                                const minDate = dayjs().subtract(120, 'year');

                                return (
                                    current &&
                                    (current > maxDate.endOf('day') || current < minDate.startOf('day'))
                                );
                            },
                            // 🔹 mở lịch ở khoảng năm hợp lý
                            defaultPickerValue: value ?? dayjs().subtract(30, 'year'),
                            onChange: (date, dateString) => {
                                onChange(createValue(name, date ? new Date(date) : null))
                            },
                        }
                    })
                )(DatePicker),
            },
            {
                label: 'gender',
                name: 'gender',
                component: compose(
                    withTranslate,
                    mapProps(({name, form, translate}) => {
                        const {value, onChange, disabled} =
                        withStudentInfo(name, form) ?? {}
                        return {
                            name,
                            value,
                            disabled,
                            onChange,
                            buttonStyle: 'solid',
                            className: 'w-full custom-radio-button',
                            children: Object.values(GenderTypes).map(
                                ({name, value: val, label}) => (
                                    <Radio.Button
                                        key={name}
                                        value={val}>
                                        {translate(label)}
                                    </Radio.Button>
                                )
                            ),
                        }
                    })
                )(Radio.Group),
            },
        ],
    },
    info_group_2: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'phone',
                name: 'contact_phone',
                component: compose(
                    withTranslate,
                    mapProps(({name, form, translate}) => {
                        const {value, onChange, disabled} =
                        withStudentInfo(name, form) ?? {}
                        return {
                            name,
                            disabled,
                            type: 'tel',
                            value: value,
                            inputMode: 'tel',
                            onChange: onChange,
                            placeholder: translate('ex: phone'),
                        }
                    })
                )(Input),
            },
            {
                label: 'email',
                name: 'contact_email',
                component: compose(
                    withTranslate,
                    mapProps(({name, form, translate}) => {
                        const {value, onChange, disabled} =
                        withStudentInfo(name, form) ?? {}
                        return {
                            name,
                            disabled,
                            value: value,
                            type: 'email',
                            inputMode: 'email',
                            onChange: onChange,
                            placeholder: translate('ex: email'),
                        }
                    })
                )(Input),
            },
        ],
    },
    course: {
        children: [
            {
                name: 'course',
                component: mapProps(({value}) => ({
                    item: value,
                }))(CourseInformation),
            },
        ],
    },
    course_class: {
        title: 'placed into a class',
        children: [
            {
                name: 'course_class',
                component: mapProps(({name, value, form, onChange}) => {
                    const {course} = form?.values ?? {}
                    return {
                        name,
                        value,
                        onChange,
                        course_id: getId(course),
                    }
                })(CourseClassSelector),
            },
        ],
    },
    identifier: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'identifier type',
                name: 'identifier_type',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        onChange,
                        buttonStyle: 'solid',
                        className: 'w-full custom-radio-button',
                        children: Object.values(IdentifierTypes).map(
                            ({name, value, label}) => (
                                <Radio.Button
                                    key={name}
                                    value={value}>
                                    {translate(label)}
                                </Radio.Button>
                            )
                        ),
                    }))
                )(Radio.Group),
            },
            {
                label: 'identifier code',
                name: 'identifier_code',
                component: createControlledFormField({
                    placeholder: 'identifier code',
                    Component: Input,
                }),
            },
        ],
    },
    metadata: {
        children: [
            {
                name: 'metadata',
                component: mapProps(({value}) => ({
                    item: value,
                }))(MetaInformation),
            },
        ],
    },
}

const RegisterStudentInformation = ({item}) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const {
        student,
        user,
        course,
        is_self_reg = 1,
        registered_subjects = [],
    } = item ?? {}

    const {
        student_idcode,
        patron_saint_name,
    } = student ?? {}

    return (
        <div className="py-2 render-border-center flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <Avatar
                    size={35}
                    src={student?.avatar || user?.avatar}
                    icon={<FaUserGraduate size={15} className="text-color-500"/>}
                    className="flex flex-center background-200"
                />
                <div className="flex flex-col flex-1">
            <span className="font-bold text-base text-color-000 leading-snug">
              {renderIf(
                  patron_saint_name,
                  <span className="mr-1 font-medium text-sm text-color-100 italic">
                    {getAliasName(student, 'patron_saint')}
                  </span>
              )}
                {getTitle(student) || getTitle(user)}
            </span>
                    {renderIf(
                        student_idcode && is_self_reg,
                        <div className="space-x-1 text-color-500 text-xs italic">
                  <span className="whitespace-no-wrap">
                    {isSm ? '#' : `${t('student code')} : `}
                  </span>
                            <span
                                onClick={() => {
                                    copyToClipboard(student_idcode)
                                    message.info(t('copied'))
                                }}
                                className="font-semibold text-blue-500 hover:text-blue-700 italic cursor-pointer">
                    {student_idcode}
                                <IoCopyOutline size={12} className="ml-1"/>
                  </span>
                        </div>
                    )}
                </div>
            </div>
            <div className="ml-2 md:ml-3 flex flex-col gap-1">
                <ItemInformation
                    showIcon={true}
                    className="text-sm"
                    label="enrolled in the course"
                    value={
                        <Link to={getLinkToDetail(course)} className="text-color-000 no-underline">
                            {getTitle(course)}
                        </Link>
                    }
                />
                <DoubleValueItem
                    showIcon={true}
                    labelFirst="contact"
                    valueFirst={student?.contact_phone}
                    labelSecond={student?.contact_email && " - " || ''}
                    valueSecond={student?.contact_email}
                    className="text-sm italic"
                />
                <div className="flex-1 space-x-1 text-sm">
                    <GoDotFill size={10} className="text-color-400"/>
                    <span className="text-color-500 whitespace-no-wrap italic">
              {`${t('subjects registered')} :`}
            </span>
                    {renderIfElse(
                        registered_subjects.length > 0,
                        registered_subjects.map((e, i) => (
                            <span key={i} className="font-semibold text-color-000">
                      {getTitle(e)} {i < registered_subjects.length - 1 && ', '}
                    </span>
                        )),
                        <span className="font-semibold text-color-300">
                  {t('not update')}
                </span>
                    )}
                </div>
            </div>
        </div>
    )

}

export const CourseApproveModal = ({
                                       owner_id,
                                       owner_type,
                                       initialValues,
                                       onCancel = NullAsDefault,
                                       onSubmit = NullAsDefault,
                                       onReject = NullAsDefault,
                                   }) => {
    const t = useTranslate()
    const [reset_key, set_reset_key] = useState()

    const validate = (values) => {
        let errors = {}
        const {fullname, course_class, student_mode, identifier_type, identifier_code} = values ?? {}
        const course_class_id = getId(course_class)

        if (_.isEmpty(fullname ?? '')) {
            errors.fullname = t('required field')
        }
        if (!course_class_id) {
            errors.course_class = t('required field')
        }

        return errors
    }

    const _initialValues = useMemo(() => {
        const {student, patron_saint, date_of_birth, ...rest} = initialValues ?? {}
        const student_mode = student ? student_modes.select : student_modes.create
        const information = information_schema.reduce(
            (prev, {name, defaultValue, transform = renderSelf}) => {
                const raw_value = _.get(initialValues, name)
                const value = transform(raw_value) ?? defaultValue
                return {
                    ...prev,
                    [name]: value,
                }
            },
            {}
        )
        return {
            ...rest,
            student,
            patron_saint,
            patron_saint_id: patron_saint?.id,
            owner_id: rest?.course?.owner?.id,
            owner_type: getType(rest?.course?.owner),
            information: {
                ...information,
                student_idcode: student?.student_idcode,
                identifier_type: rest?.identifier_type,
                identifier_code: rest?.identifier_code,
            },
            student_mode,
            student_info: information,
            prev_student_info: information,
            date_of_birth: parseDayjs(date_of_birth),
        }
    }, [initialValues, owner_id, owner_type])

    const {is_self_reg = 1} = initialValues ?? {}

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            onReset={() => {
                set_reset_key(Date.now())
            }}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {
                    user,
                    ready,
                    student,
                    owner_id,
                    owner_type,
                    information,
                    course_class,
                    student_info,
                    student_mode,
                    prev_student_info,
                    ...rest
                } = changes ?? {}
                const course_class_id = values?.course_class
                    ? getId(course_class)
                    : null
                const student_id =
                    student_mode === student_modes.select && values?.student
                        ? getId(values?.student)
                        : null
                onSubmit(
                    _.omitBy(
                        {
                            ...(student_info ?? {}),
                            ...rest,
                            student_id,
                            course_class_id,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1 mb-4">
                    {renderIfElse(
                        is_self_reg,
                        <RegisterStudentInformation item={initialValues}/>,
                        <EnrollmentInfo item={initialValues} className="ml-3"/>
                    )}
                    <div className="ml-3">
                        <FieldsFactory formSchema={[formSchema.metadata]}/>
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <FieldsFactory formSchema={[formSchema.course_class]}/>
                    <span className="text-sm text-color-500 italic">
              {`* ${t('placed into a class description')}`}
            </span>
                </div>

                <FormikConsumer>
                    {({values, setValues, handleChange}) => {
                        const {student, student_mode} = values
                        return (
                            <React.Fragment>
                                <Pure input={[reset_key]}>
                                    <DidOnMount
                                        key={[reset_key]}
                                        handler={() => {
                                            handleChange(createValue('ready', true))
                                        }}
                                    />
                                </Pure>
                                <Radio.Group
                                    name="student_mode"
                                    onChange={(event) => {
                                        const {name, value} = event.target ?? {}
                                        setValues({
                                            ...values,
                                            [name]: value,
                                            ...(value === student_modes.select
                                                ? {
                                                    student_info: student,
                                                }
                                                : {
                                                    student_info: values.prev_student_info,
                                                    prev_student_info: values.student_info,
                                                }),
                                        })
                                    }}
                                    defaultValue={student_mode}
                                    className="custom-radio-button border-b py-2"
                                    options={[
                                        {label: t('select student'), value: student_modes.select},
                                        {label: t('create new'), value: student_modes.create},
                                    ]}
                                />
                                <Pure input={[student_mode]}>
                                    {renderIf(
                                        student_mode === student_modes.select,
                                        <FieldsFactory
                                            formSchema={[
                                                formSchema.student_list,
                                            ]}
                                        />
                                    )}
                                </Pure>
                                <Pure input={[student_mode]}>
                                    {renderIf(
                                        student_mode === student_modes.create,
                                        <FieldsFactory
                                            formSchema={[
                                                formSchema.patron_saint__fullname,
                                                formSchema.info_group_1,
                                                formSchema.info_group_2,
                                                formSchema.identifier,
                                                formSchema.notes,
                                            ]}
                                        />
                                    )}
                                </Pure>
                            </React.Fragment>
                        )
                    }}
                </FormikConsumer>
                <div className="flex items-center gap-2">
                    <div
                        onClick={() => (
                            showDeleteConfirmDialog({
                                icon: <IoWarning className="anticon"/>,
                                translate: t,
                                title: 'reject student',
                                message: 'are you sure you want to reject this student',
                                onOk: () => {
                                    onReject()
                                },
                            })
                        )}
                        style={{height: 28, padding: '6px 16px', fontSize: 13}}
                        className="flex bg-orange-100 hover:bg-orange-200 rounded-md font-medium text-orange-400 hover:text-orange-600 leading-tight cursor-pointer">
                        {t('reject')}
                    </div>
                    <div className="flex-1"/>
                    <FormActionBar submitText="approve"/>
                </div>
            </div>
        </Formik>
    )
}
